// src/pages/client/AppointmentDetailsPage.tsx
import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../hooks/useRedux';
import { Appointment, CheckInEligibility } from '../../types/appointment';
import { formatCurrency } from '../../utils/formatters';
import appointmentService from '../../services/appointmentService';
import realtimeService from '../../services/realtimeService';
import { mergeAppointmentUpdate } from '../../utils/appointmentRealtime';
import { useDocumentVisibleRefetch } from '../../hooks/useDocumentVisibleRefetch';
import {
  cancellationConfirmButtonLabel,
  formatCancellationConfirmBody,
  formatCancellationSuccessMessage,
} from '../../utils/cancellationConfirmCopy';
import { getAppointmentGrandTotal, getAddOnsSubtotalDollars } from '../../utils/appointmentPricing';
import { format, parseISO, isAfter, addHours, addDays } from 'date-fns';
import { getConfirmDownPaymentDisplay } from '../../utils/downPaymentDisplay';
import { createConversation, getConversationsWithUnreadCounts, getConversations, getConversation } from '../../services/chatService';
import { useUI } from '../../context/UIContext';
import ReviewForm from '../../components/client/ReviewForm';
import ClientReviewForm from '../../components/stylist/ClientReviewForm';
import reviewService, { Review } from '../../services/reviewService';
import IncomingAppointmentReview from '../../components/appointments/IncomingAppointmentReview';
import {
  acceptReviewForSlot,
  buildAppointmentReviewSections,
} from '../../utils/appointmentReviewDirection';
import EditAddOnsModal from '../../components/EditAddOnsModal';
import DiscountBadge from '../../components/common/DiscountBadge';

// SVG Icons
const CalendarIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
    <line x1="16" y1="2" x2="16" y2="6"></line>
    <line x1="8" y1="2" x2="8" y2="6"></line>
    <line x1="3" y1="10" x2="21" y2="10"></line>
  </svg>
);

const TimeIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"></circle>
    <polyline points="12 6 12 12 16 14"></polyline>
  </svg>
);

const LocationIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
    <circle cx="12" cy="10" r="3"></circle>
  </svg>
);

const PaymentIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect>
    <line x1="1" y1="10" x2="23" y2="10"></line>
  </svg>
);

const NotesIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
    <polyline points="14 2 14 8 20 8"></polyline>
    <line x1="16" y1="13" x2="8" y2="13"></line>
    <line x1="16" y1="17" x2="8" y2="17"></line>
    <polyline points="10 9 9 9 8 9"></polyline>
  </svg>
);

const RescheduleIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
  </svg>
);

const CancelIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"></circle>
    <line x1="15" y1="9" x2="9" y2="15"></line>
    <line x1="9" y1="9" x2="15" y2="15"></line>
  </svg>
);

const ExpandMoreIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 12 15 18 9"></polyline>
  </svg>
);

const MapIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6"></polygon>
    <line x1="8" y1="2" x2="8" y2="18"></line>
    <line x1="16" y1="6" x2="16" y2="22"></line>
  </svg>
);

const ArrowBackIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="19" y1="12" x2="5" y2="12"></line>
    <polyline points="12 19 5 12 12 5"></polyline>
  </svg>
);

const ChatIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
  </svg>
);

const MailIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
    <polyline points="22,6 12,13 2,6"></polyline>
  </svg>
);

const CheckCircleIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
    <polyline points="22 4 12 14.01 9 11.01"></polyline>
  </svg>
);

const ReceiptIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 10V7a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v3"></path>
    <polyline points="17 21 17 13 7 13 7 21"></polyline>
    <polyline points="7 3 7 8 15 8"></polyline>
  </svg>
);

// Mock date picker since @mui/x-date-pickers may not be compatible
const StaticDatePicker = (props: any) => {
  return (
    <div className="appointment-details__dialog-date-picker">
      <div>Date Picker Mock</div>
      <div>Selected date: {props.value?.toDateString()}</div>
    </div>
  );
};


// Mock time slots for rescheduling
const mockTimeSlots = [
  "09:00", "09:30", "10:00", "10:30", "11:00", "11:30",
  "13:00", "13:30", "14:00", "14:30", "15:00", "15:30", "16:00", "16:30"
];

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function escapeHtmlReceipt(text: string) {
  return String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

const getAppointmentUserId = (person?: Appointment['client'] | Appointment['stylist']) =>
  person?._id || (person as any)?.id || '';

const getAppointmentUserName = (
  person: Appointment['client'] | Appointment['stylist'] | undefined,
  fallback: string
) => person?.name || fallback;

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`details-tabpanel-${index}`}
      aria-labelledby={`details-tab-${index}`}
      className={`appointment-details__tab-panel ${value === index ? 'appointment-details__tab-panel--active' : ''}`}
    >
      {children}
    </div>
  );
};

/** Minimum time "Updating status..." stays visible before the overlay closes. */
const MIN_UPDATING_STATUS_DISPLAY_MS = 400;

const AppointmentDetailsPage: React.FC = () => {
  const navigate = useNavigate();
  const { shortCode } = useParams<{ shortCode: string }>();
  const publicRef = shortCode;
  const { user } = useAppSelector((state) => state.auth);
  const { setLoading: setUILoading, showSnackbar, showLoading, updateLoadingMessage, hideLoading } = useUI();
  const completingServiceRef = useRef(false);
  
  // Use local state like mobile version instead of Redux
  const [appointment, setAppointment] = useState<Appointment | null>(null);
  const appointmentId = appointment?._id || appointment?.id || publicRef;
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const [tabValue, setTabValue] = useState(0);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancellationSummary, setCancellationSummary] = useState<{
    canCancel: boolean;
    reason?: string;
    appointmentStatus?: string;
    hoursUntilAppointment?: number;
    refundAmount?: number;
    refundPercentage?: number;
    cancellationFee?: number;
    cancellationFeePercentage?: number;
    appliedRule?: string;
    warning?: string;
    message?: string;
    gracePeriodApplied?: boolean;
  } | null>(null);
  const [rescheduleDialogOpen, setRescheduleDialogOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<string | null>(null);
  const [paymentDetailsOpen, setPaymentDetailsOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState<number>(0);
  
  // Review state
  const [stylistReview, setStylistReview] = useState<Review | null>(null);
  const [clientReview, setClientReview] = useState<Review | null>(null);
  const [showStylistReviewForm, setShowStylistReviewForm] = useState(false);
  const [showClientReviewForm, setShowClientReviewForm] = useState(false);
  const [, setLoadingReviews] = useState(false);
  
  // Add-ons editing state
  const [showEditAddOnsModal, setShowEditAddOnsModal] = useState(false);

  // Client check-in state
  const [checkInEligibility, setCheckInEligibility] = useState<CheckInEligibility | null>(null);
  const [showCannotCheckInModal, setShowCannotCheckInModal] = useState(false);
  const [showVeryEarlyModal, setShowVeryEarlyModal] = useState(false);
  const [showLateModal, setShowLateModal] = useState(false);
  const [checkInLoading, setCheckInLoading] = useState(false);

  // Stylist workflow state (detail page)
  const [checkInEligibilityStylist, setCheckInEligibilityStylist] = useState<CheckInEligibility | null>(null);
  const [showCheckInPinSection, setShowCheckInPinSection] = useState(false);
  const [stylistPinInput, setStylistPinInput] = useState('');
  const [statusUpdateLoading, setStatusUpdateLoading] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  // Fetch appointment directly like mobile version
  const fetchAppointment = async (options?: { silent?: boolean }) => {
    if (!publicRef) {
      setError('No appointment ID provided');
      setIsLoading(false);
      return;
    }

    try {
      if (!options?.silent) setIsLoading(true);
      setError(null);
      const response = await appointmentService.getAppointment(publicRef);
      setAppointment(response);
    } catch (err: any) {
      console.error('Failed to fetch appointment:', err);
      if (!options?.silent) {
        const errorMessage = err.response?.data?.message || err.message || 'Failed to load appointment details';
        setError(errorMessage);
      }
    } finally {
      if (!options?.silent) setIsLoading(false);
    }
  };

  const appointmentRef = useRef<Appointment | null>(null);
  appointmentRef.current = appointment;
  const fetchAppointmentRef = useRef(fetchAppointment);
  fetchAppointmentRef.current = fetchAppointment;

  useEffect(() => {
    return realtimeService.onAppointmentUpdate((data) => {
      const current = appointmentRef.current;
      const currentId = current?._id || current?.id;
      const eventId = data?.appointmentId || data?.appointment?._id || data?.appointment?.id;
      if (!currentId || !eventId || String(eventId) !== String(currentId)) return;
      setAppointment((prev) => (prev ? mergeAppointmentUpdate(prev, data) : prev));
      fetchAppointmentRef.current({ silent: true });
    });
  }, []);

  useDocumentVisibleRefetch(() => {
    if (!appointmentRef.current) return;
    fetchAppointmentRef.current({ silent: true });
  }, Boolean(publicRef));

  // Fetch reviews for this appointment
  const fetchReviews = useCallback(async () => {
    if (!appointmentId) return;

    try {
      setLoadingReviews(true);
      const [stylistReviewData, clientReviewData] = await Promise.all([
        reviewService.getAppointmentReview(appointmentId, 'stylist-review'),
        reviewService.getAppointmentReview(appointmentId, 'client-review')
      ]);

      setStylistReview(acceptReviewForSlot(stylistReviewData, 'stylist-review'));
      setClientReview(acceptReviewForSlot(clientReviewData, 'client-review'));
    } catch (err) {
      console.error('Failed to fetch reviews:', err);
    } finally {
      setLoadingReviews(false);
    }
  }, [appointmentId]);

  // Fetch unread message count for this appointment
  const fetchUnreadCount = useCallback(async () => {
    if (!appointmentId || !user) return;
    
    const userId = user?.id || (user as any)?._id;
    if (!userId) return;
    
    // Get the appointment ID - use appointment._id if available, otherwise use appointmentId from URL
    const aptId = appointment?._id || appointment?.id || appointmentId;
    
    try {
      // Get all conversations with unread counts (optimized backend endpoint)
      const conversations = await getConversationsWithUnreadCounts();
      
      // Find the conversation for this appointment - compare as strings to handle ObjectId vs string
      const appointmentConvo = conversations.find(
        (c) => c.type === 'appointment' && String(c.appointment) === String(aptId)
      );

      if (appointmentConvo && appointmentConvo.unreadCount !== undefined) {
        setUnreadCount(appointmentConvo.unreadCount);
      } else {
        // Fallback: fetch conversation and count manually
        const allConversations = await getConversations();
        const convo = allConversations.find(
          (c) => c.type === 'appointment' && String(c.appointment) === String(aptId)
        );
        
        if (convo) {
          const convoDetails = await getConversation(convo._id);
          let count = 0;
          for (const msg of convoDetails.messages) {
            if (!msg.readBy.includes(userId) && msg.sender !== userId) {
              count++;
            }
          }
          setUnreadCount(count);
        } else {
          setUnreadCount(0);
        }
      }
    } catch (err) {
      console.error('Error fetching unread message count:', err);
      setUnreadCount(0);
    }
  }, [appointmentId, user, appointment]);

  useEffect(() => {
    fetchAppointment();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publicRef]);

  useEffect(() => {
    if (appointment && appointmentId) {
      const status = appointment.status;
      if (status === 'completed') {
        fetchReviews();
      }
    }
  }, [appointment, appointmentId, fetchReviews]);

  // Fetch unread count when appointment is loaded
  useEffect(() => {
    if (appointment && appointmentId) {
      fetchUnreadCount();
    }
  }, [appointment, appointmentId, fetchUnreadCount]);

  // Refresh unread count periodically
  useEffect(() => {
    if (!appointment) return;
    
    const interval = setInterval(() => {
      fetchUnreadCount();
    }, 30000); // Refresh every 30 seconds

    return () => clearInterval(interval);
  }, [appointment, fetchUnreadCount]);

  // Fetch check-in eligibility when client and appointment is confirmed
  useEffect(() => {
    if (!appointmentId || !appointment || user?.userType !== 'client') {
      setCheckInEligibility(null);
      return;
    }
    const status = appointment.status;
    if (status !== 'confirmed') {
      setCheckInEligibility(null);
      return;
    }
    appointmentService.getCheckInEligibility(appointmentId).then(setCheckInEligibility).catch(() => setCheckInEligibility(null));
  }, [appointmentId, appointment, user?.userType]);

  const handleTabChange = async (newValue: number) => {
    // If Chat tab is clicked and there are unread messages, open chat immediately
    if (newValue === 2 && unreadCount > 0) {
      await handleOpenChat();
      return; // Don't change tab value since we're navigating away
    }
    
    setTabValue(newValue);
  };

  const formatAppointmentDate = useCallback((dateTime?: string) => {
    if (!dateTime) return 'N/A';
    try {
      return format(parseISO(dateTime), 'EEEE, MMMM d, yyyy');
    } catch (error) {
      return dateTime;
    }
  }, []);

  const formatAppointmentTime = (dateTime?: string) => {
    if (!dateTime) return 'N/A';
    try {
      return format(parseISO(dateTime), 'h:mm a');
    } catch (error) {
      return dateTime;
    }
  };

  const handleDownloadReceipt = useCallback(() => {
    if (!appointment) return;
    const grand = getAppointmentGrandTotal(appointment);
    const service =
      appointment.service?.finalPrice ?? appointment.service?.originalPrice ?? 0;
    const dateStr = formatAppointmentDate(appointment.dateTime || appointment.date);
    const addonRows = (appointment.addons || [])
      .map(
        (a) =>
          `<tr><td>${escapeHtmlReceipt(a.name || 'Add-on')}</td><td style="text-align:right">${formatCurrency(
            (a.priceCents || 0) / 100
          )}</td></tr>`
      )
      .join('');
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>LookBel Receipt</title>
<style>body{font-family:system-ui,-apple-system,sans-serif;max-width:520px;margin:24px;color:#111}
table{border-collapse:collapse;width:100%;margin:16px 0} td{padding:6px 0;border-bottom:1px solid #eee}
.total{font-weight:700;margin-top:16px;font-size:1.1rem}
@media print {
  body { margin: 0; }
  @page { size: A5; margin: 20mm; }
}</style></head><body>
<div style="text-align:center;margin-bottom:24px">
  <h2 style="color:#C9A84C;margin:0">LookBel</h2>
  <p style="color:#666;font-size:12px;margin:4px 0">lookbel.com</p>
</div>
<h1 style="font-size:1.25rem">Receipt</h1>
${appointment.shortCode ? `<p>REC-${escapeHtmlReceipt(appointment.shortCode)}</p>` : ''}
<p>${escapeHtmlReceipt(dateStr)}</p>
<table>
<tr><td>Service</td><td style="text-align:right">${formatCurrency(service)}</td></tr>
${
  addonRows
    ? `<tr><td colspan="2" style="padding-top:12px;font-weight:600">Add-ons</td></tr>${addonRows}`
    : ''
}
</table>
<p class="total">Total paid: ${formatCurrency(grand)}</p>
<p style="color:#666;font-size:12px;margin-top:24px">Loyalty points earned (estimate): ${Math.floor(
      grand / 10
    )}</p>
</body></html>`;
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 500);
  }, [appointment, formatAppointmentDate]);

  const getStatusClass = (status: string) => {
    switch (status) {
      case 'confirmed':
        return 'confirmed';
      case 'pending':
        return 'pending';
      case 'checked-in':
      case 'in-progress':
        return 'in-progress';
      case 'cancelled':
      case 'expired':
        return 'cancelled';
      case 'completed':
        return 'completed';
      case 'no-show':
        return 'cancelled';
      case 'rescheduled':
        return 'rescheduled';
      default:
        return '';
    }
  };

  const isUpcoming = (apt: Appointment) => {
    if (!apt?.dateTime) {
      return false;
    }
    const status = apt.status;
    return (
      (status === 'pending' || status === 'confirmed') &&
      isAfter(parseISO(apt.dateTime), new Date())
    );
  };

  const canCancel = (apt: Appointment) => {
    // Allow cancellation if appointment is upcoming and at least 24 hours in advance
    if (!apt?.dateTime) {
      return false;
    }
    return (
      isUpcoming(apt) &&
      isAfter(
        parseISO(apt.dateTime),
        addHours(new Date(), 24)
      )
    );
  };

  const canReschedule = (apt: Appointment) => {
    // Allow rescheduling if appointment is upcoming and at least 24 hours in advance
    if (!apt?.dateTime) {
      return false;
    }
    return (
      isUpcoming(apt) &&
      isAfter(
        parseISO(apt.dateTime),
        addHours(new Date(), 24)
      )
    );
  };

  const cancellationNotice = useMemo(() => {
    if (!cancellationSummary) return null;
    return (
      <div className="appointment-details__dialog-body-notice" style={{ whiteSpace: 'pre-line' }}>
        {formatCancellationConfirmBody(cancellationSummary)}
      </div>
    );
  }, [cancellationSummary]);

  const handleOpenCancelDialog = async () => {
    if (!appointmentId) return;
    
    try {
      // Fetch cancellation summary
      const summary = await appointmentService.getCancellationSummary(appointmentId);
      
      if (!summary.canCancel) {
        showSnackbar(summary.reason || 'This appointment cannot be cancelled at this time.', 'error');
        return;
      }

      // Store summary so the dialog and handleCancelAppointment can use it
      setCancellationSummary(summary);
      setCancelDialogOpen(true);
      
    } catch (error) {
      console.error('Error fetching cancellation summary:', error);
      // Fallback: open dialog without summary on network/auth errors
      setCancellationSummary(null);
      setCancelDialogOpen(true);
    }
  };

  const handleCloseCancelDialog = () => {
    setCancelDialogOpen(false);
    setCancelReason('');
    setCancellationSummary(null);
  };

  const handleCancelAppointment = async () => {
    if (!appointmentId) return;
    try {
      await appointmentService.cancelAppointment(appointmentId, {
        reason: cancelReason || 'client-request',
        notes: cancelReason || 'Cancelled by client',
      });
      setCancelDialogOpen(false);
      setCancelReason('');

      const summary = cancellationSummary;
      setCancellationSummary(null);
      const successMsg = summary
        ? formatCancellationSuccessMessage(summary)
        : 'Appointment cancelled.';

      await fetchAppointment();
      showSnackbar(successMsg, 'success');
    } catch (error) {
      console.error('Failed to cancel appointment:', error);
      showSnackbar('Failed to cancel appointment. Please try again.', 'error');
    }
  };

  const handleOpenRescheduleDialog = () => {
    if (appointment) {
      if (appointment.dateTime) {
        setSelectedDate(parseISO(appointment.dateTime));
        setSelectedTimeSlot(format(parseISO(appointment.dateTime), 'HH:mm'));
      } else if ((appointment as any).date) {
        setSelectedDate(parseISO((appointment as any).date));
      }
      if ((appointment as any).time && !appointment.dateTime) {
        setSelectedTimeSlot((appointment as any).time);
      }
      setRescheduleDialogOpen(true);
    }
  };

  const handleCloseRescheduleDialog = () => {
    setRescheduleDialogOpen(false);
    setSelectedDate(null);
    setSelectedTimeSlot(null);
  };

  /**
   * Open chat - create or retrieve conversation and navigate to chat page
   */
  const handleOpenChat = async () => {
    if (!appointment) return;
    const stylistId = getAppointmentUserId(appointment.stylist);
    const clientId = getAppointmentUserId(appointment.client);
    if (!stylistId || !clientId) {
      showSnackbar('Unable to start chat: missing participant information', 'error');
      return;
    }

    try {
      setUILoading(true);
      const participants = [stylistId, clientId];
      
      const chatConversation = await createConversation(
        participants,
        'appointment',
        appointmentId || (appointment as any)?._id || appointment.id
      );
      
      setUILoading(false);
      showSnackbar('Opening chat...', 'success');
      
      // Navigate to chat page
      const userType = user?.userType || 'client';
      navigate(`/${userType}/chat/${chatConversation._id}`);
    } catch (error: any) {
      console.error('Error opening chat:', error);
      setUILoading(false);
      showSnackbar(error.message || 'Failed to open chat', 'error');
    }
  };

  // Browser geolocation for client check-in
  const getCurrentLocation = (): Promise<{ coordinates: [number, number]; accuracy: number }> => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation is not supported by your browser.'));
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            coordinates: [position.coords.longitude, position.coords.latitude],
            accuracy: position.coords.accuracy ?? 0
          });
        },
        (err) => reject(err),
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
      );
    });
  };

  const performClientCheckIn = async (confirmFlags?: { veryEarlyAccepted?: boolean; lateAccepted?: boolean }) => {
    if (!appointmentId) return;
    setCheckInLoading(true);
    try {
      setUILoading(true);
      const locationData = await getCurrentLocation();
      await appointmentService.checkIn(appointmentId, {
        method: 'gps',
        location: locationData,
        confirmFlags
      });
      await fetchAppointment();
      setShowVeryEarlyModal(false);
      setShowLateModal(false);
      showSnackbar('You are checked in.', 'success');
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Failed to check in.';
      showSnackbar(msg, 'error');
    } finally {
      setCheckInLoading(false);
      setUILoading(false);
    }
  };

  const handleCheckIn = async () => {
    if (!checkInEligibility?.allowed) {
      setShowCannotCheckInModal(true);
      return;
    }
    if (checkInEligibility.confirmVeryEarly) {
      setShowVeryEarlyModal(true);
      return;
    }
    if (checkInEligibility.confirmLate) {
      setShowLateModal(true);
      return;
    }
    await performClientCheckIn();
  };

  // Stylist workflow handlers (confirm → check-in → start → complete)
  const handleStylistConfirm = () => {
    if (!appointmentId) return;
    setShowConfirmDialog(true);
  };

  const handleConfirmAppointment = async () => {
    if (!appointmentId) return;
    setShowConfirmDialog(false);
    setStatusUpdateLoading(true);
    try {
      setUILoading(true);
      const updated = await appointmentService.updateAppointmentStatus(appointmentId, { status: 'confirmed' });
      setAppointment(updated);
      showSnackbar('Appointment confirmed.', 'success');
    } catch (err: any) {
      showSnackbar(err?.response?.data?.message || 'Failed to confirm.', 'error');
    } finally {
      setStatusUpdateLoading(false);
      setUILoading(false);
    }
  };

  const handleStylistDecline = async () => {
    const reasons = [
      { label: 'Time slot no longer available', value: 'time-unavailable' },
      { label: 'Service not offered', value: 'service-not-offered' },
      { label: 'Location too far', value: 'distance-too-far' },
      { label: 'Schedule conflict', value: 'calendar-conflict' },
      { label: 'Other', value: 'other' },
    ];
    const choice = window.prompt(
      'Select a reason: ' + reasons.map((r, i) => `${i + 1}. ${r.label}`).join(', ') + '\nEnter number (1-5):'
    );
    if (choice == null) return;
    const idx = parseInt(choice, 10) - 1;
    if (idx < 0 || idx >= reasons.length) {
      showSnackbar('Invalid selection.', 'error');
      return;
    }
    if (!appointmentId || !window.confirm(`Decline with reason: ${reasons[idx].label}?`)) return;
    setStatusUpdateLoading(true);
    try {
      setUILoading(true);
      await appointmentService.rejectAppointment(appointmentId, {
        reason: reasons[idx].value,
        notes: reasons[idx].label,
      });
      await fetchAppointment();
      showSnackbar('Appointment declined. Client has been notified.', 'success');
    } catch (err: any) {
      showSnackbar(err?.response?.data?.message || 'Failed to decline.', 'error');
    } finally {
      setStatusUpdateLoading(false);
      setUILoading(false);
    }
  };

  const handleStylistCheckInClick = async () => {
    if (!appointmentId) return;
    setStatusUpdateLoading(true);
    try {
      const eligibility = await appointmentService.getCheckInEligibility(appointmentId);
      setCheckInEligibilityStylist(eligibility);
      if (eligibility.requiresPin || eligibility.requiresPinForLate) {
        setShowCheckInPinSection(true);
        showSnackbar(eligibility.requiresPinForLate ? 'Check-in window passed. Request a PIN from the client.' : 'This window requires a PIN. Request one below.', 'info');
        return;
      }
      if (!eligibility.allowed) {
        showSnackbar(eligibility.reason === 'TOO_LATE' ? 'Check-in window has passed. Request a PIN to check in anyway.' : (eligibility.reason || 'Check-in not allowed.'), 'error');
        setShowCheckInPinSection(true);
        return;
      }
      setUILoading(true);
      await appointmentService.checkIn(appointmentId, { method: 'stylist' });
      await fetchAppointment();
      setShowCheckInPinSection(false);
      showSnackbar('Client checked in.', 'success');
    } catch (err: any) {
      const code = err?.response?.data?.code;
      if (code === 'PIN_REQUIRED' || code === 'TOO_LATE') {
        setShowCheckInPinSection(true);
        const eligibility = await appointmentService.getCheckInEligibility(appointmentId).catch(() => null);
        if (eligibility) setCheckInEligibilityStylist(eligibility);
      }
      showSnackbar(err?.response?.data?.message || 'Check-in failed.', 'error');
    } finally {
      setStatusUpdateLoading(false);
      setUILoading(false);
    }
  };

  const handleRequestPinDetail = async () => {
    if (!appointmentId) return;
    setStatusUpdateLoading(true);
    try {
      await appointmentService.requestCheckInPin(appointmentId);
      const eligibility = await appointmentService.getCheckInEligibility(appointmentId);
      setCheckInEligibilityStylist(eligibility);
      showSnackbar('PIN requested. Client will see it in the app. Ask them for the code.', 'success');
    } catch (err: any) {
      showSnackbar(err?.response?.data?.message || 'Failed to request PIN.', 'error');
    } finally {
      setStatusUpdateLoading(false);
    }
  };

  const handleVerifyPinDetail = async () => {
    if (!appointmentId || !stylistPinInput.trim()) {
      showSnackbar('Enter the 6-digit PIN from the client.', 'error');
      return;
    }
    setStatusUpdateLoading(true);
    try {
      setUILoading(true);
      await appointmentService.verifyCheckInPin(appointmentId, stylistPinInput.trim());
      setStylistPinInput('');
      setShowCheckInPinSection(false);
      await fetchAppointment();
      showSnackbar('Client checked in.', 'success');
    } catch (err: any) {
      showSnackbar(err?.response?.data?.message || 'Invalid or expired PIN.', 'error');
    } finally {
      setStatusUpdateLoading(false);
      setUILoading(false);
    }
  };

  const handleStartService = async () => {
    if (!appointmentId || !window.confirm('Start service for this appointment?')) return;
    setStatusUpdateLoading(true);
    try {
      setUILoading(true);
      const updated = await appointmentService.startService(appointmentId);
      setAppointment(updated);
      showSnackbar('Service started.', 'success');
    } catch (err: any) {
      showSnackbar(err?.response?.data?.message || 'Failed to start service.', 'error');
    } finally {
      setStatusUpdateLoading(false);
      setUILoading(false);
    }
  };

  const handleCompleteService = async () => {
    if (!appointmentId || completingServiceRef.current) return;
    if (!window.confirm('Process payment and mark this appointment as completed?')) return;
    if (completingServiceRef.current) return;

    // One PUT /appointments/:id/complete charges and completes. The second
    // overlay line is local pacing while that result is applied.
    completingServiceRef.current = true;
    setStatusUpdateLoading(true);
    let completedAppointment: Appointment | null = null;
    try {
      showLoading('Processing payment...');
      const updated = await appointmentService.completeService(appointmentId);
      updateLoadingMessage('Updating status...');
      await new Promise<void>((resolve) => {
        window.setTimeout(resolve, MIN_UPDATING_STATUS_DISPLAY_MS);
      });
      setAppointment(updated);
      completedAppointment = updated;
    } catch (err: any) {
      const serverMessage = err?.response?.data?.message;
      showSnackbar(
        typeof serverMessage === 'string' && serverMessage.trim()
          ? serverMessage
          : 'Failed to update status',
        'error'
      );
    } finally {
      hideLoading();
      completingServiceRef.current = false;
      setStatusUpdateLoading(false);
    }

    if (completedAppointment) {
      showSnackbar('Appointment completed.', 'success');
    }
  };

  const handleMarkNoShow = async () => {
    if (!appointmentId || !window.confirm('Mark this client as no-show? They will be notified.')) return;
    setStatusUpdateLoading(true);
    try {
      setUILoading(true);
      await appointmentService.markAsNoShow(appointmentId, 'Client did not arrive');
      await fetchAppointment();
      showSnackbar('Marked as no-show.', 'success');
    } catch (err: any) {
      showSnackbar(err?.response?.data?.message || 'Failed to mark no-show.', 'error');
    } finally {
      setStatusUpdateLoading(false);
      setUILoading(false);
    }
  };

  const handleClientConfirmCompletion = async () => {
    if (!appointmentId || !window.confirm('Confirm that this service was completed to your satisfaction?')) return;
    setStatusUpdateLoading(true);
    try {
      setUILoading(true);
      const updated = await appointmentService.confirmCompletion(appointmentId);
      setAppointment(updated);
      showSnackbar('Thank you for confirming.', 'success');
    } catch (err: any) {
      showSnackbar(err?.response?.data?.message || 'Failed to confirm.', 'error');
    } finally {
      setStatusUpdateLoading(false);
      setUILoading(false);
    }
  };

  // const handleRescheduleAppointment = () => {
  //   if (appointmentId && selectedDate && selectedTimeSlot) {
  //     dispatch(rescheduleAppointment({
  //       appointmentId,
  //       date: format(selectedDate, 'yyyy-MM-dd'),
  //       time: selectedTimeSlot
  //     }));
  //     setRescheduleDialogOpen(false);
  //   }
  // };


  const togglePaymentDetails = () => {
    setPaymentDetailsOpen(!paymentDetailsOpen);
  };

  // Show error first if there's an error
  if (error) {
    return (
      <div className="container">
        <div className="appointment-details__error">
          <h2 className="appointment-details__error-title">Error loading appointment details</h2>
          <p className="appointment-details__error-message">{error}</p>
          <button className="appointment-details__error-button" onClick={() => navigate(-1)}>
            <ArrowBackIcon /> Go Back
          </button>
        </div>
      </div>
    );
  }

  // Show loading if still loading or no appointment data yet
  if (isLoading || !appointment) {
    return (
      <div className="container">
        <div className="appointment-details__loading">
          <div className="appointment-details__loading-back">
            <div className="appointment-details__loading-back-button"></div>
            <div className="appointment-details__loading-back-title"></div>
          </div>
          <div className="appointment-details__loading-content">
            <div className="appointment-details__loading-content-header"></div>
            <div className="appointment-details__loading-content-subtitle"></div>
            <div className="appointment-details__loading-grid">
              <div className="appointment-details__loading-block"></div>
              <div className="appointment-details__loading-block"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!appointment) return null;

  const statusStr = appointment.status;
  const activeStatuses = ['pending', 'confirmed', 'checked-in', 'in-progress'];
  const showStylistWorkflow = user?.userType === 'stylist' && activeStatuses.includes(statusStr);
  const needClientConfirmCompletion =
    user?.userType === 'client' &&
    statusStr === 'completed' &&
    appointment?.serviceCompletion?.stylistMarked &&
    !appointment?.serviceCompletion?.clientConfirmed;
  const showActions =
    (user?.userType === 'client' && (isUpcoming(appointment) || needClientConfirmCompletion)) ||
    showStylistWorkflow;

  const clientId = getAppointmentUserId(appointment.client);
  const stylistId = getAppointmentUserId(appointment.stylist);
  const clientName = getAppointmentUserName(appointment.client, 'Client');
  const stylistName = getAppointmentUserName(appointment.stylist, 'Stylist');
  const contactName = user?.userType === 'stylist' ? clientName : stylistName;
  const canOpenChat = Boolean(clientId && stylistId);

  return (
    <div className="container">
      <div className="appointment-details">
        {/* Header */}
        <div className="appointment-details__header">
          <button 
            className="appointment-details__header-back" 
            onClick={() => navigate(-1)}
            aria-label="Go back"
          >
            <ArrowBackIcon />
          </button>
          <h1 className="appointment-details__header-title">Appointment Details</h1>
        </div>
        
        <div className="appointment-details__content">
          {/* Status banner */}
          <div className={`appointment-details__status-banner appointment-details__status-banner--${getStatusClass(appointment.status)}`}>
            <div className="appointment-details__status-label">
              <CheckCircleIcon />
              {(() => {
                const status = appointment.status;
                return status.charAt(0).toUpperCase() + status.slice(1);
              })()}
            </div>
            <div className="appointment-details__price-tag">
              {appointment.service?.originalPrice && appointment.service.originalPrice !== appointment.service?.finalPrice && (
                <span style={{ textDecoration: 'line-through', color: 'rgba(255,255,255,0.65)', marginRight: 6, fontSize: '0.85em' }}>
                  {formatCurrency(appointment.service.originalPrice)}
                </span>
              )}
              {formatCurrency(
                (appointment.service?.finalPrice || appointment.service?.originalPrice || 0) +
                ((appointment.addons?.reduce((sum: number, addon: any) => sum + (typeof addon.priceCents === 'number' ? addon.priceCents : 0), 0) || 0) / 100)
              )}
            </div>
          </div>
          {/* Discount badges */}
          {appointment.discountSource && appointment.discountSource !== 'none' && (
            <div style={{ padding: '8px 16px 0' }}>
              <DiscountBadge
                discountSource={appointment.discountSource}
                promotionApplied={appointment.promotionApplied}
                originalPrice={appointment.service?.originalPrice}
                finalPrice={appointment.service?.finalPrice}
                showSavings={true}
              />
            </div>
          )}
          
          {/* Tabs */}
          <div className="appointment-details__tabs">
            <button 
              className={`appointment-details__tab ${tabValue === 0 ? 'appointment-details__tab--active' : ''}`}
              onClick={() => handleTabChange(0)}
              role="tab"
              aria-selected={tabValue === 0}
              id="details-tab-0"
              aria-controls="details-tabpanel-0"
            >
              Details
            </button>
            <button 
              className={`appointment-details__tab ${tabValue === 1 ? 'appointment-details__tab--active' : ''}`}
              onClick={() => handleTabChange(1)}
              role="tab"
              aria-selected={tabValue === 1}
              id="details-tab-1"
              aria-controls="details-tabpanel-1"
            >
              Location
            </button>
            <button 
              className={`appointment-details__tab ${tabValue === 2 ? 'appointment-details__tab--active' : ''}`}
              onClick={() => handleTabChange(2)}
              role="tab"
              aria-selected={tabValue === 2}
              id="details-tab-2"
              aria-controls="details-tabpanel-2"
            >
              Chat
              {unreadCount > 0 && (
                <span className="appointment-details__tab-badge">{unreadCount}</span>
              )}
            </button>
          </div>
          
          {/* Details Tab */}
          <TabPanel value={tabValue} index={0}>
            <div className="appointment-details__grid">
              {/* Service and Stylist Info */}
              <div>
                <div className="appointment-details__service">
                  <h2 className="appointment-details__service-name">
                    {appointment.service?.name || "Service"}
                  </h2>
                  <p className="appointment-details__service-duration">
                    {appointment.service?.duration || 30} minutes
                  </p>
                  {(() => {
                    const service = appointment.service as any;
                    const description = 
                      service?.stylistService?.service?.description ||
                      service?.stylistService?.description ||
                      service?.description;
                    
                    let displayText = '';
                    if (typeof description === 'string') {
                      displayText = description;
                    } else if (typeof description === 'object' && description !== null) {
                      // If description is an object, try to extract 'short', 'long', or 'text' fields
                      displayText = description.short || description.long || description.text || '';
                    }
                    
                    return displayText && (
                      <p className="appointment-details__service-description">
                        {displayText}
                      </p>
                    );
                  })()}
                </div>

                {/* Add-ons Section */}
                <div className="appointment-details__addons-section">
                  <div className="appointment-details__addons-header">
                    <h3 className="appointment-details__addons-title">Add-ons</h3>
                    <div className="appointment-details__addons-header-actions">
                      {['pending', 'confirmed', 'checked-in', 'in-progress'].includes(
                        appointment.status
                      ) && (
                        <button
                          className="appointment-details__edit-addons-btn"
                          onClick={() => {
                          // Check permissions before opening modal
                          const status = appointment.status;
                          const editableStatuses = ['pending', 'confirmed', 'checked-in', 'in-progress'];
                          if (!editableStatuses.includes(status)) {
                            alert(`Cannot edit add-ons for ${status} appointments`);
                            return;
                          }

                          // Check client-specific restrictions
                          if (user?.userType === 'client') {
                            const editCount = appointment.addOnEditCount || 0;
                            if (editCount >= 2) {
                              alert('You have reached the maximum number of add-on edits (2). Please contact your stylist for further changes.');
                              return;
                            }

                            const appointmentTime = new Date(appointment.dateTime);
                            const now = new Date();
                            const hoursUntilAppointment = (appointmentTime.getTime() - now.getTime()) / (1000 * 60 * 60);

                            if (hoursUntilAppointment < 12) {
                              alert('You cannot edit add-ons at this time. Please contact your stylist to make changes.');
                              return;
                            }
                          }

                          setShowEditAddOnsModal(true);
                        }}
                      >
                        Edit
                        {user?.userType === 'client' && appointment.addOnEditCount !== undefined && (
                          <span className="appointment-details__edit-count"> ({appointment.addOnEditCount}/2)</span>
                        )}
                      </button>
                    )}
                    </div>
                  </div>
                  {appointment.addons && appointment.addons.length > 0 ? (
                    <div className="appointment-details__addons-list">
                      {appointment.addons.map((addon, index) => (
                        <div key={index} className="appointment-details__addon-item">
                          <div className="appointment-details__addon-info">
                            <span className="appointment-details__addon-name">{addon.name}</span>
                            {addon.description && (
                              <span className="appointment-details__addon-description">{addon.description}</span>
                            )}
                          </div>
                          <div className="appointment-details__addon-details">
                            {addon.durationMinutes > 0 && (
                              <span className="appointment-details__addon-duration">+{addon.durationMinutes} min</span>
                            )}
                            <span className="appointment-details__addon-price">
                              {addon.sourceType === 'custom' && addon.priceCents === 0
                                ? 'TBD'
                                : formatCurrency(addon.priceCents / 100)
                              }
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="appointment-details__empty-addons">No add-ons selected</p>
                  )}
                </div>
                
                {/* Show client info if user is stylist, stylist info if user is client */}
                {user?.userType === 'stylist' ? (
                  <div className="appointment-details__stylist">
                    <img 
                      src={appointment.client?.profilePhoto || `https://ui-avatars.com/api/?name=${encodeURIComponent(appointment.client?.name || 'Client')}&size=64&background=random`}
                      alt={appointment.client?.name || "Client"}
                      className="appointment-details__stylist-avatar"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        if (!target.src.includes('ui-avatars.com')) {
                          target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(appointment.client?.name || 'Client')}&size=64&background=random`;
                        }
                      }}
                    />
                    <div className="appointment-details__stylist-info">
                      <span className="appointment-details__stylist-name">
                        {appointment.client?.name || "Client"}
                      </span>
                      <span className="appointment-details__stylist-title">
                        Client
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className="appointment-details__stylist">
                    <img 
                      src={appointment.stylist?.profilePhoto || `https://ui-avatars.com/api/?name=${encodeURIComponent(appointment.stylist?.name || 'Stylist')}&size=64&background=random`}
                      alt={appointment.stylist?.name || "Stylist"}
                      className="appointment-details__stylist-avatar"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        if (!target.src.includes('ui-avatars.com')) {
                          target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(appointment.stylist?.name || 'Stylist')}&size=64&background=random`;
                        }
                      }}
                    />
                    <div className="appointment-details__stylist-info">
                      <span className="appointment-details__stylist-name">
                        {appointment.stylist?.name || "Stylist"}
                      </span>
                      <span className="appointment-details__stylist-title">
                        Stylist
                      </span>
                    </div>
                  </div>
                )}
                
                <hr className="divider" />
                
                {/* Date and Time */}
                <div className="appointment-details__detail-item">
                  <CalendarIcon />
                  <span className="appointment-details__detail-item-text">
                    {formatAppointmentDate(appointment.dateTime || appointment.date)}
                  </span>
                </div>
                <div className="appointment-details__detail-item">
                  <TimeIcon />
                  <span className="appointment-details__detail-item-text">
                    {formatAppointmentTime(appointment.dateTime || appointment.time)}
                  </span>
                </div>
                
                {/* Payment info */}
                <div className="appointment-details__accordion">
                  <div 
                    className="appointment-details__accordion-header" 
                    onClick={togglePaymentDetails}
                  >
                    <h3 className="appointment-details__accordion-title">
                      <PaymentIcon />
                      Payment Details
                    </h3>
                    <div className={`appointment-details__accordion-icon ${paymentDetailsOpen ? 'appointment-details__accordion-icon--open' : ''}`}>
                      <ExpandMoreIcon />
                    </div>
                  </div>
                  <div className={`appointment-details__accordion-content ${paymentDetailsOpen ? 'appointment-details__accordion-content--open' : ''}`}>
                    {/* Original price row when discount applied */}
                    {appointment.service?.originalPrice && appointment.service.originalPrice !== appointment.service?.finalPrice && (
                      <div className="appointment-details__accordion-row">
                        <span className="appointment-details__accordion-row-label">Service (original)</span>
                        <span className="appointment-details__accordion-row-value" style={{ textDecoration: 'line-through', color: '#999' }}>
                          {formatCurrency(appointment.service.originalPrice)}
                        </span>
                      </div>
                    )}
                    <div className="appointment-details__accordion-row">
                      <span className="appointment-details__accordion-row-label">Service</span>
                      <span className="appointment-details__accordion-row-value">{formatCurrency(appointment.service?.finalPrice || appointment.service?.originalPrice || 0)}</span>
                    </div>
                    {appointment.addons && appointment.addons.length > 0 && (
                      <div className="appointment-details__accordion-row">
                        <span className="appointment-details__accordion-row-label">
                          Add-ons ({appointment.addons.length})
                        </span>
                        <span className="appointment-details__accordion-row-value">
                          {formatCurrency(appointment.addons.reduce((sum: number, addon: any) => sum + (typeof addon.priceCents === 'number' ? addon.priceCents : 0), 0) / 100)}
                        </span>
                      </div>
                    )}
                    {/* Promotion discount row */}
                    {appointment.discountSource === 'promotion' && appointment.promotionApplied && (
                      <div className="appointment-details__accordion-row">
                        <span className="appointment-details__accordion-row-label" style={{ color: '#C9A84C' }}>
                          🎁 {appointment.promotionApplied.title}
                          {appointment.promotionApplied.discountType === 'percentage'
                            ? ` (${appointment.promotionApplied.discountValue}% off)`
                            : ''}
                        </span>
                        <span className="appointment-details__accordion-row-value" style={{ color: '#16a34a' }}>
                          -{formatCurrency(appointment.promotionApplied.discountAmount)}
                        </span>
                      </div>
                    )}
                    {/* Loyalty discount row
                      I3: For 'loyalty'-only, the whole price delta = loyalty discount.
                      For 'both' (legacy), the delta includes promo too, so we can only show
                      the combined saving since individual amounts aren't stored separately. */}
                    {(appointment.discountSource === 'loyalty' || appointment.discountSource === 'both') && (
                      <div className="appointment-details__accordion-row">
                        <span className="appointment-details__accordion-row-label" style={{ color: '#D97706' }}>
                          ⭐ {appointment.discountSource === 'both' ? 'Combined Discount' : 'Loyalty Discount'}
                        </span>
                        <span className="appointment-details__accordion-row-value" style={{ color: '#16a34a' }}>
                          {(() => {
                            const stored = (appointment as any).loyaltyRedemption?.discountAmount;
                            if (typeof stored === 'number' && stored > 0) {
                              return `-${formatCurrency(stored)}`;
                            }
                            if (
                              appointment.service?.originalPrice &&
                              appointment.service.originalPrice > (appointment.service?.finalPrice || 0) &&
                              appointment.discountSource === 'loyalty'
                            ) {
                              return `-${formatCurrency(
                                appointment.service.originalPrice - (appointment.service?.finalPrice || 0)
                              )}`;
                            }
                            return '—';
                          })()}
                        </span>
                      </div>
                    )}
                    {(appointment as any).loyaltyPointsUsed > 0 && !appointment.discountSource && (
                      <div className="appointment-details__accordion-row">
                        <span className="appointment-details__accordion-row-label">Loyalty Points Used</span>
                        <span className="appointment-details__accordion-row-value">-{formatCurrency(((appointment as any).loyaltyPointsUsed || 0) / 10)}</span>
                      </div>
                    )}
                    <div className="appointment-details__accordion-row appointment-details__accordion-row--total">
                      <span className="appointment-details__accordion-row-label">Total</span>
                      <span className="appointment-details__accordion-row-value">
                        {formatCurrency(
                          (appointment.service?.finalPrice || appointment.service?.originalPrice || 0) +
                          ((appointment.addons?.reduce((sum: number, addon: any) => sum + (typeof addon.priceCents === 'number' ? addon.priceCents : 0), 0) || 0) / 100)
                        )}
                      </span>
                    </div>
                    <div className="appointment-details__accordion-row">
                      <span className="appointment-details__accordion-row-label">Status</span>
                      <span className={`appointments__status appointments__status--${getStatusClass(appointment.paymentStatus || 'pending')}`}>
                        {(appointment.paymentStatus || 'pending').charAt(0).toUpperCase() + (appointment.paymentStatus || 'pending').slice(1)}
                      </span>
                    </div>
                  </div>
                </div>
                
                {/* Notes */}
                {appointment.notes && (
                  <div className="appointment-details__notes">
                    <div className="appointment-details__notes-header">
                      <NotesIcon />
                      <h3 className="appointment-details__notes-title">Notes</h3>
                    </div>
                    {typeof appointment.notes === 'string' ? (
                      <p className="appointment-details__notes-content">
                        {appointment.notes}
                      </p>
                    ) : (
                      <div className="appointment-details__notes-content">
                        {appointment.notes.client && (
                          <div className="appointment-details__notes-item">
                            <h4>Client Notes:</h4>
                            <p>{appointment.notes.client}</p>
                          </div>
                        )}
                        {appointment.notes.stylist && (
                          <div className="appointment-details__notes-item">
                            <h4>Stylist Notes:</h4>
                            <p>{appointment.notes.stylist}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              {/* Actions and Receipt */}
              <div>
                {showActions && (
                  <div className="appointment-details__actions">
                    <h3 className="appointment-details__actions-title">
                      Appointment Actions
                    </h3>
                    <div className="appointment-details__actions-grid">
                      {/* Stylist workflow: Confirm → Check In → Start Service → Complete */}
                      {user?.userType === 'stylist' && statusStr === 'pending' && (
                        <>
                          <button
                            className="appointment-details__actions-button appointment-details__actions-button--primary"
                            onClick={handleStylistConfirm}
                            disabled={statusUpdateLoading}
                          >
                            {statusUpdateLoading ? '…' : 'Confirm'}
                          </button>
                          <button
                            className="appointment-details__actions-button appointment-details__actions-button--danger"
                            onClick={handleStylistDecline}
                            disabled={statusUpdateLoading}
                          >
                            Decline
                          </button>
                        </>
                      )}
                      {user?.userType === 'stylist' && statusStr === 'confirmed' && (
                        <>
                          <button
                            className="appointment-details__actions-button appointment-details__actions-button--primary"
                            onClick={handleStylistCheckInClick}
                            disabled={statusUpdateLoading}
                          >
                            {statusUpdateLoading ? '…' : 'Check In'}
                          </button>
                          {(showCheckInPinSection || checkInEligibilityStylist?.hasActivePin) && (
                            <div className="appointment-details__actions-pin-row">
                              <button
                                type="button"
                                className="appointment-details__actions-button"
                                onClick={handleRequestPinDetail}
                                disabled={statusUpdateLoading}
                              >
                                Request PIN
                              </button>
                              <input
                                type="text"
                                inputMode="numeric"
                                maxLength={6}
                                placeholder="PIN from client"
                                value={stylistPinInput}
                                onChange={(e) => setStylistPinInput(e.target.value.replace(/\D/g, ''))}
                                className="appointment-details__input appointment-details__input--pin"
                              />
                              <button
                                type="button"
                                className="appointment-details__actions-button appointment-details__actions-button--primary"
                                onClick={handleVerifyPinDetail}
                                disabled={statusUpdateLoading || !stylistPinInput.trim()}
                              >
                                Verify & Check In
                              </button>
                            </div>
                          )}
                          {appointmentService.canMarkAsNoShow(appointment) && (
                            <button
                              className="appointment-details__actions-button appointment-details__actions-button--danger"
                              onClick={handleMarkNoShow}
                              disabled={statusUpdateLoading}
                            >
                              Mark No-Show
                            </button>
                          )}
                        </>
                      )}
                      {user?.userType === 'stylist' && statusStr === 'checked-in' && (
                        <>
                          <button
                            className="appointment-details__actions-button appointment-details__actions-button--primary"
                            onClick={handleStartService}
                            disabled={statusUpdateLoading}
                          >
                            {statusUpdateLoading ? '…' : 'Start Service'}
                          </button>
                          {appointmentService.canMarkAsNoShow(appointment) && (
                            <button
                              className="appointment-details__actions-button appointment-details__actions-button--danger"
                              onClick={handleMarkNoShow}
                              disabled={statusUpdateLoading}
                            >
                              Mark No-Show
                            </button>
                          )}
                        </>
                      )}
                      {user?.userType === 'stylist' && statusStr === 'in-progress' && (
                        <button
                          type="button"
                          className="appointment-details__actions-button appointment-details__actions-button--primary"
                          onClick={handleCompleteService}
                          disabled={statusUpdateLoading}
                          aria-label="Complete Service"
                        >
                          {statusUpdateLoading ? '…' : 'Complete Service'}
                        </button>
                      )}
                      {/* Client: Check In */}
                      {user?.userType === 'client' && statusStr === 'confirmed' && (
                        <button
                          className="appointment-details__actions-button appointment-details__actions-button--primary"
                          onClick={handleCheckIn}
                          disabled={checkInLoading}
                        >
                          {checkInLoading ? 'Checking in…' : 'Check In'}
                        </button>
                      )}
                      {/* Client: Confirm Completion (after stylist marked completed) */}
                      {needClientConfirmCompletion && (
                        <button
                          className="appointment-details__actions-button appointment-details__actions-button--primary"
                          onClick={handleClientConfirmCompletion}
                          disabled={statusUpdateLoading}
                        >
                          {statusUpdateLoading ? '…' : 'Confirm Completion'}
                        </button>
                      )}
                      <button
                        className="appointment-details__actions-button"
                        onClick={handleOpenChat}
                      >
                        <ChatIcon /> Chat
                        {unreadCount > 0 && (
                          <span className="appointment-details__unread-indicator" title={`${unreadCount} unread message${unreadCount > 1 ? 's' : ''}`}>
                            <MailIcon />
                            <span className="appointment-details__unread-badge">{unreadCount}</span>
                          </span>
                        )}
                      </button>
                      {user?.userType === 'client' && canReschedule(appointment) && (
                        <button
                          className="appointment-details__actions-button"
                          onClick={handleOpenRescheduleDialog}
                        >
                          <RescheduleIcon /> Reschedule
                        </button>
                      )}
                      {user?.userType === 'client' && canCancel(appointment) && (
                        <button
                          className="appointment-details__actions-button appointment-details__actions-button--danger"
                          onClick={handleOpenCancelDialog}
                        >
                          <CancelIcon /> Cancel
                        </button>
                      )}
                    </div>
                  </div>
                )}
                
                {/* Receipt for completed appointments */}
                {(() => {
                  const status = appointment.status;
                  if (status !== 'completed') return null;
                  const grandTotal = getAppointmentGrandTotal(appointment);
                  const addOnsTotal = getAddOnsSubtotalDollars(appointment);
                  const servicePrice =
                    appointment.service?.finalPrice ?? appointment.service?.originalPrice ?? 0;
                  const addOnList = appointment.addons || [];
                  return (
                  <div className="appointment-details__receipt">
                    <div className="appointment-details__receipt-header">
                      <ReceiptIcon />
                      <h3 className="appointment-details__receipt-header-title">
                        Receipt
                      </h3>
                    </div>
                    {appointment.shortCode && (
                    <div className="appointment-details__receipt-row">
                      <span className="appointment-details__receipt-row-label">Receipt #</span>
                      <span className="appointment-details__receipt-row-value">REC-{appointment.shortCode}</span>
                    </div>
                    )}
                    <div className="appointment-details__receipt-divider"></div>
                    <div className="appointment-details__receipt-row">
                      <span className="appointment-details__receipt-row-label">Date</span>
                      <span className="appointment-details__receipt-row-value">{formatAppointmentDate(appointment.dateTime || appointment.date)}</span>
                    </div>
                    <div className="appointment-details__receipt-row">
                      <span className="appointment-details__receipt-row-label">Payment Method</span>
                      <span className="appointment-details__receipt-row-value">Credit Card ****4589</span>
                    </div>
                    <div className="appointment-details__receipt-divider"></div>
                    <div className="appointment-details__receipt-row">
                      <span className="appointment-details__receipt-row-label">Service</span>
                      <span className="appointment-details__receipt-row-value">{formatCurrency(servicePrice)}</span>
                    </div>
                    {addOnsTotal > 0 && (
                      <>
                        <div className="appointment-details__receipt-row">
                          <span className="appointment-details__receipt-row-label">
                            Add-ons {addOnList.length ? `(${addOnList.length})` : ''}
                          </span>
                          <span className="appointment-details__receipt-row-value">{formatCurrency(addOnsTotal)}</span>
                        </div>
                        {addOnList.map((addon) => (
                          <div
                            key={addon._id || `${addon.name}-${addon.priceCents}`}
                            className="appointment-details__receipt-row appointment-details__receipt-row--indent"
                          >
                            <span className="appointment-details__receipt-row-label">{addon.name}</span>
                            <span className="appointment-details__receipt-row-value">
                              {formatCurrency((addon.priceCents || 0) / 100)}
                            </span>
                          </div>
                        ))}
                      </>
                    )}
                    <div className="appointment-details__receipt-row">
                      <span className="appointment-details__receipt-row-label">Loyalty Points Earned</span>
                      <span className="appointment-details__receipt-row-value">{Math.floor(grandTotal / 10)}</span>
                    </div>
                    <div className="appointment-details__receipt-divider"></div>
                    <div className="appointment-details__receipt-row appointment-details__receipt-row--total">
                      <span className="appointment-details__receipt-row-label">Total Paid</span>
                      <span className="appointment-details__receipt-row-value">
                        {formatCurrency(grandTotal)}
                      </span>
                    </div>
                    <button
                      type="button"
                      className="appointment-details__receipt-button"
                      onClick={handleDownloadReceipt}
                    >
                      <ReceiptIcon /> Download Receipt
                    </button>
                  </div>
                  );
                })()}
                
                {/* Two independent review directions for a completed appointment. */}
                {appointment.status === 'completed' && (() => {
                  const role = user?.userType === 'client' ? 'client' : 'stylist';
                  const sections = buildAppointmentReviewSections(
                    role,
                    stylistReview,
                    clientReview,
                    user
                  );
                  const ownReview = sections.ownReview as Review | null;

                  return (
                    <>
                      <div className="appointment-details__review">
                        <h3 className="appointment-details__review-title">
                          {sections.ownHeading}
                        </h3>

                        {role === 'client' ? (
                          <>
                            {sections.showWriteAction && !showStylistReviewForm && (
                              <button
                                className="appointment-details__review-button"
                                onClick={() => setShowStylistReviewForm(true)}
                              >
                                Write a Review
                              </button>
                            )}

                            {(showStylistReviewForm || ownReview) && (
                              stylistId ? (
                                <ReviewForm
                                  stylistId={stylistId}
                                  stylistName={stylistName}
                                  stylistPhoto={appointment.stylist?.profilePhoto}
                                  appointmentId={appointmentId}
                                  existingReview={ownReview}
                                  onSuccess={() => {
                                    setShowStylistReviewForm(false);
                                    fetchReviews();
                                  }}
                                  onCancel={() => setShowStylistReviewForm(false)}
                                />
                              ) : (
                                <p className="appointment-details__empty-addons">Stylist information is unavailable for this appointment.</p>
                              )
                            )}
                          </>
                        ) : (
                          <>
                            {sections.showWriteAction && !showClientReviewForm && (
                              <button
                                className="appointment-details__review-button"
                                onClick={() => setShowClientReviewForm(true)}
                              >
                                Write a Review
                              </button>
                            )}

                            {(showClientReviewForm || ownReview) && (
                              clientId ? (
                                <ClientReviewForm
                                  clientId={clientId}
                                  clientName={clientName}
                                  clientPhoto={appointment.client?.profilePhoto}
                                  appointmentId={appointmentId!}
                                  existingReview={ownReview}
                                  onSuccess={() => {
                                    setShowClientReviewForm(false);
                                    fetchReviews();
                                  }}
                                  onCancel={() => setShowClientReviewForm(false)}
                                />
                              ) : (
                                <p className="appointment-details__empty-addons">Client information is unavailable for this appointment.</p>
                              )
                            )}
                          </>
                        )}
                      </div>

                      {sections.incomingReview && sections.incomingHeading && (
                        <IncomingAppointmentReview
                          title={sections.incomingHeading}
                          authorName={sections.incomingAuthorName}
                          review={sections.incomingReview}
                        />
                      )}
                    </>
                  );
                })()}

                {/* Contact Info - Show client info if stylist, stylist info if client */}
                <div className="appointment-details__contact">
                  <h3 className="appointment-details__contact-title">
                    Contact Information
                  </h3>
                  <ul className="appointment-details__contact-list">
                    <li className="appointment-details__contact-item">
                      <span className="appointment-details__contact-label">Phone</span>
                      <p className="appointment-details__contact-value">
                        {user?.userType === 'stylist' 
                          ? (appointment.client?.phoneNumber || 'N/A')
                          : (appointment.stylist?.phoneNumber || 'N/A')}
                      </p>
                    </li>
                    <li className="appointment-details__contact-item">
                      <span className="appointment-details__contact-label">Email</span>
                      <p className="appointment-details__contact-value">
                        {user?.userType === 'stylist'
                          ? (appointment.client?.email || 'N/A')
                          : (appointment.stylist?.email || 'N/A')}
                      </p>
                    </li>
                    <li className="appointment-details__contact-item">
                      <button
                        className="appointment-details__contact-message-button"
                        onClick={handleOpenChat}
                        disabled={!canOpenChat}
                      >
                        {unreadCount > 0 && (
                          <span className="appointment-details__contact-unread-indicator" title={`${unreadCount} unread message${unreadCount > 1 ? 's' : ''}`}>
                            <MailIcon />
                            <span className="appointment-details__contact-unread-badge">{unreadCount}</span>
                          </span>
                        )}
                        <ChatIcon /> Send Message
                      </button>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </TabPanel>
          
          {/* Location Tab */}
          <TabPanel value={tabValue} index={1}>
            <div className="appointment-details__location">
              {appointment.isMobileService && appointment.location ? (
                <>
                  <h3 className="appointment-details__location-title">
                    Mobile Service Location
                  </h3>
                  <div className="appointment-details__location-address">
                    <LocationIcon />
                    <div className="appointment-details__location-address-text">
                      {appointment.location.address && (
                        <p>{appointment.location.address}{appointment.location.unit ? `, Unit ${appointment.location.unit}` : ''}</p>
                      )}
                      {(appointment.location.city || appointment.location.state) && (
                        <p>{appointment.location.city || ''}{appointment.location.city && appointment.location.state ? ', ' : ''}{appointment.location.state || ''} {appointment.location.zipCode || ''}</p>
                      )}
                    </div>
                  </div>
                  {appointment.location.address && (
                    <a
                      className="appointment-details__location-button"
                      href={`https://maps.google.com/?q=${encodeURIComponent(
                        `${appointment.location.address}${appointment.location.unit ? `, Unit ${appointment.location.unit}` : ''}, ${appointment.location.city || ''}, ${appointment.location.state || ''} ${appointment.location.zipCode || ''}`
                      )}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <MapIcon /> Open in Maps
                    </a>
                  )}
                </>
              ) : appointment.stylist?.location ? (
                <>
                  <h3 className="appointment-details__location-title">
                    {user?.userType === 'stylist' ? 'Service Location' : `${appointment.stylist?.name || 'Stylist'}'s Salon`}
                  </h3>
                  <div className="appointment-details__location-address">
                    <LocationIcon />
                    <div className="appointment-details__location-address-text">
                      {appointment.stylist.location.address && (
                        <p>{appointment.stylist.location.address}{appointment.stylist.location.unit ? `, Unit ${appointment.stylist.location.unit}` : ''}</p>
                      )}
                      {(appointment.stylist.location.city || appointment.stylist.location.state) && (
                        <p>{appointment.stylist.location.city || ''}{appointment.stylist.location.city && appointment.stylist.location.state ? ', ' : ''}{appointment.stylist.location.state || ''} {appointment.stylist.location.zipCode || ''}</p>
                      )}
                    </div>
                  </div>
                  {appointment.stylist.location.address && (
                    <a
                      className="appointment-details__location-button"
                      href={`https://maps.google.com/?q=${encodeURIComponent(
                        `${appointment.stylist.location.address}${appointment.stylist.location.unit ? `, Unit ${appointment.stylist.location.unit}` : ''}, ${appointment.stylist.location.city || ''}, ${appointment.stylist.location.state || ''} ${appointment.stylist.location.zipCode || ''}`
                      )}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <MapIcon /> Open in Maps
                    </a>
                  )}
                </>
              ) : (
                <div className="appointment-details__location-empty">
                  <LocationIcon />
                  <p>Location information not available</p>
                </div>
              )}
              
              {/* Map placeholder */}
              <div className="appointment-details__location-map">
                <MapIcon />
                <span className="appointment-details__location-map-label">Map View</span>
              </div>
              
              <div className="appointment-details__location-info">
                <h4 className="appointment-details__location-info-title">
                  Parking Information
                </h4>
                <p className="appointment-details__location-info-text">
                  There is a small parking lot behind the salon. Street parking is also available in the area.
                </p>
              </div>
              
              <div className="appointment-details__location-info">
                <h4 className="appointment-details__location-info-title">
                  Public Transportation
                </h4>
                <p className="appointment-details__location-info-text">
                  The salon is accessible by bus routes 22 and 33. The nearest bus stop is 2 blocks away.
                </p>
              </div>
            </div>
          </TabPanel>
          
          {/* Chat Tab */}
          <TabPanel value={tabValue} index={2}>
            <div className="appointment-details__chat-empty">
              <ChatIcon />
              <h3 className="appointment-details__chat-empty-title">
                Chat with {contactName}
              </h3>
              <p className="appointment-details__chat-empty-text">
                Start a conversation about this appointment.
              </p>
              <button
                className="appointment-details__chat-empty-button"
                onClick={handleOpenChat}
              >
                <ChatIcon /> Open Chat
              </button>
            </div>
          </TabPanel>
        </div>
      </div>
      
      {/* Stylist Confirm Dialog */}
      {appointment && (
        <div className={`appointment-details__dialog ${showConfirmDialog ? 'appointment-details__dialog--open' : ''}`}>
          <div className="appointment-details__dialog-content">
            <div className="appointment-details__dialog-header">
              <h2 className="appointment-details__dialog-header-title">Confirm Appointment</h2>
            </div>
            <div className="appointment-details__dialog-body">
              <p className="appointment-details__dialog-body-text">
                Are you sure you want to confirm this appointment for <strong>{appointment.service?.name}</strong> with <strong>{clientName}</strong>?
              </p>
              {(() => {
                const policy = (appointment.paymentDetails as any)?.downPaymentPolicySnapshot;
                const totalPrice = (appointment as any).totalPriceWithAddOns
                  || appointment.service?.finalPrice
                  || appointment.service?.originalPrice
                  || 0;
                const { enabled: dpEnabled, percentage, amount: dpAmount } =
                  getConfirmDownPaymentDisplay(policy, totalPrice);
                return (
                  <div className="appointment-details__dialog-body-notice appointment-details__dialog-body-notice--confirm">
                    {dpEnabled && dpAmount > 0 ? (
                      <p>💳 A <strong>{percentage}% booking guarantee of {formatCurrency(dpAmount)}</strong> is quoted. The client is not charged at confirmation — that amount is charged only if they cancel late or no-show. The full service amount is charged at completion.</p>
                    ) : (
                      <p>ℹ️ No booking guarantee required for this appointment.</p>
                    )}
                  </div>
                );
              })()}
            </div>
            <div className="appointment-details__dialog-footer">
              <button
                className="appointment-details__dialog-button appointment-details__dialog-button--secondary"
                onClick={() => setShowConfirmDialog(false)}
                disabled={statusUpdateLoading}
              >
                Cancel
              </button>
              <button
                className="appointment-details__dialog-button appointment-details__dialog-button--primary"
                onClick={handleConfirmAppointment}
                disabled={statusUpdateLoading}
              >
                {statusUpdateLoading ? '…' : 'Confirm Appointment'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Dialog */}
      <div className={`appointment-details__dialog ${cancelDialogOpen ? 'appointment-details__dialog--open' : ''}`}>
        <div className="appointment-details__dialog-content">
          <div className="appointment-details__dialog-header">
            <h2 className="appointment-details__dialog-header-title">Cancel Appointment</h2>
          </div>
          <div className="appointment-details__dialog-body">
            <p className="appointment-details__dialog-body-text">
              Are you sure you want to cancel your appointment for {appointment.service?.name || 'service'} with {(user?.userType === 'stylist' ? appointment.client?.name : appointment.stylist?.name) || 'user'} on {formatAppointmentDate(appointment.dateTime || appointment.date)} at {formatAppointmentTime(appointment.dateTime || appointment.time)}?
            </p>
            {/* Charge preview from server policy */}
            {cancellationNotice}
            {!cancellationSummary && (
              <p className="appointment-details__dialog-body-notice">
                Please note: A cancellation fee may apply depending on the stylist's policy.
              </p>
            )}
            <textarea
              className="appointment-details__dialog-textarea"
              placeholder="Reason for cancellation (optional)"
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              rows={3}
            />
          </div>
          <div className="appointment-details__dialog-footer">
            <button 
              className="appointment-details__dialog-button appointment-details__dialog-button--secondary"
              onClick={handleCloseCancelDialog}
              disabled={isLoading}
            >
              Keep Appointment
            </button>
            <button 
              className="appointment-details__dialog-button appointment-details__dialog-button--danger"
              onClick={handleCancelAppointment}
              disabled={isLoading}
            >
              {cancellationConfirmButtonLabel(
                cancellationSummary || {},
                'Cancel Anyway',
                'Cancel Appointment'
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Client: Cannot Check In modal */}
      {showCannotCheckInModal && (
        <div className="appointment-details__modal-overlay" onClick={() => setShowCannotCheckInModal(false)}>
          <div className="appointment-details__modal" onClick={e => e.stopPropagation()}>
            <h2 className="appointment-details__dialog-header-title">Cannot Check In</h2>
            <p className="appointment-details__modal-text">
              This appointment hasn&apos;t been confirmed yet. Please wait for your stylist to confirm your booking.
            </p>
            <div className="appointment-details__modal-actions">
              <button className="appointment-details__dialog-button" onClick={() => { setShowCannotCheckInModal(false); handleOpenChat(); }}>
                Contact Stylist
              </button>
              <button className="appointment-details__dialog-button appointment-details__dialog-button--danger" onClick={() => { setShowCannotCheckInModal(false); handleOpenCancelDialog(); }}>
                Cancel Appointment
              </button>
              <button className="appointment-details__dialog-button appointment-details__dialog-button--secondary" onClick={() => setShowCannotCheckInModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Client: Very Early Check-In modal */}
      {showVeryEarlyModal && appointment && (
        <div className="appointment-details__modal-overlay" onClick={() => setShowVeryEarlyModal(false)}>
          <div className="appointment-details__modal" onClick={e => e.stopPropagation()}>
            <h2 className="appointment-details__dialog-header-title">Very Early Check-In</h2>
            <p className="appointment-details__modal-text">
              Your appointment is at {formatAppointmentTime(appointment.dateTime || appointment.time)}. You&apos;re checking in early. Your stylist may not be ready yet. Proceed anyway?
            </p>
            <div className="appointment-details__modal-actions">
              <button className="appointment-details__dialog-button appointment-details__dialog-button--primary" onClick={() => performClientCheckIn({ veryEarlyAccepted: true })} disabled={checkInLoading}>
                {checkInLoading ? 'Checking in…' : 'Yes, check in now'}
              </button>
              <button className="appointment-details__dialog-button appointment-details__dialog-button--secondary" onClick={() => setShowVeryEarlyModal(false)}>
                Remind me later
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Client: Late Arrival modal */}
      {showLateModal && (
        <div className="appointment-details__modal-overlay" onClick={() => setShowLateModal(false)}>
          <div className="appointment-details__modal" onClick={e => e.stopPropagation()}>
            <h2 className="appointment-details__dialog-header-title">Late Arrival Notice</h2>
            <p className="appointment-details__modal-text">
              You&apos;re checking in late. Your stylist has been notified. Proceed to check in?
            </p>
            <div className="appointment-details__modal-actions">
              <button className="appointment-details__dialog-button appointment-details__dialog-button--primary" onClick={() => performClientCheckIn({ lateAccepted: true })} disabled={checkInLoading}>
                {checkInLoading ? 'Checking in…' : 'Yes, check in'}
              </button>
              <button className="appointment-details__dialog-button appointment-details__dialog-button--secondary" onClick={() => setShowLateModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reschedule Dialog */}
      <div className={`appointment-details__dialog ${rescheduleDialogOpen ? 'appointment-details__dialog--open' : ''}`}>
        <div className="appointment-details__dialog-content">
          <div className="appointment-details__dialog-header">
            <h2 className="appointment-details__dialog-header-title">Reschedule Appointment</h2>
          </div>
          <div className="appointment-details__dialog-body">
            <p className="appointment-details__dialog-body-text">
              Select a new date and time for your {appointment.service?.name || 'service'} with {stylistName}.
            </p>
            
            <StaticDatePicker
              value={selectedDate}
              onChange={(newDate: Date | null) => setSelectedDate(newDate)}
              minDate={addDays(new Date(), 1)}
            />
            
            <h3 className="appointment-details__dialog-subtitle">Available Time Slots</h3>
            <div className="appointment-details__dialog-time-slots">
              {mockTimeSlots.map((slot) => (
                <button
                  key={slot}
                  className={`appointment-details__dialog-time-slot ${
                    selectedTimeSlot === slot ? 'appointment-details__dialog-time-slot--selected' : ''
                  }`}
                  onClick={() => setSelectedTimeSlot(slot)}
                >
                  {formatAppointmentTime(slot)}
                </button>
              ))}
            </div>
          </div>
          <div className="appointment-details__dialog-footer">
            <button 
              className="appointment-details__dialog-button appointment-details__dialog-button--secondary"
              onClick={handleCloseRescheduleDialog}
            >
              Cancel
            </button>
            <button 
              className="appointment-details__dialog-button appointment-details__dialog-button--primary"
            //  onClick={handleRescheduleAppointment}
              disabled={!selectedDate || !selectedTimeSlot}
            >
              Reschedule
            </button>
          </div>
        </div>
      </div>

      {/* Edit Add-ons Modal */}
      {appointment && (
        <EditAddOnsModal
          isOpen={showEditAddOnsModal}
          onClose={() => setShowEditAddOnsModal(false)}
          appointmentId={appointmentId || appointment._id || appointment.id || ''}
          currentAddOns={appointment.addons || []}
          serviceCategory={(appointment.service as any)?.stylistService?.service?.category}
          stylistServiceId={(appointment.service as any)?.stylistService?._id || (appointment.service as any)?.stylistService || ''}
          servicePrice={appointment.service?.finalPrice || appointment.service?.originalPrice || 0}
          hasDownPayment={appointment.paymentStatus === 'partial'}
          downPaymentAmount={(appointment.paymentDetails as any)?.downPaymentAmount || 0}
          paymentStatus={appointment.paymentStatus}
          userType={user?.userType === 'stylist' ? 'stylist' : 'client'}
          editCount={appointment.addOnEditCount || 0}
          onSave={fetchAppointment}
        />
      )}
    </div>
  );
};

export default AppointmentDetailsPage;
